import { DatePicker, DateTimePicker, TimePicker } from "@mui/x-date-pickers";
import { TextField } from "@mui/material";
import { useMemo } from "react";
import { DateTimeProperty } from "types/Property";
import { TooltipFormControl } from "./TooltipFormControl"

export type DateFormControlProps = {
    value?: Date
    property: DateTimeProperty,
    onChange?: (newValue: Date | null) => void
}

export function DateFormControl({ value, property, onChange }: DateFormControlProps) {
    const label = useMemo(() => {
        let label = property.displayName || property.name;
        if (property.readOnly)
            label += " (readonly)"
        return label;
    }, [property.name, property.readOnly, property.displayName])

    if (property.hidden) { return null; }

    const handleChange = (value: Date | null) => {
        if (property.readOnly) {
            console.warn(`Field ${property.name} is read-only and cannot be changed.`);
            return;
        }
        onChange?.(value);
    };

    if (property.type === "date")
        return <TooltipFormControl property={property} >
            <DatePicker
                readOnly={property.readOnly}
                label={label}
                value={getDateTimeValue(value || property.default)}
                onChange={handleChange}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        data-testid={`field-${property.name}`}
                        aria-label={`field-${property.name}`}
                        sx={{ width: '100%', '& .MuiInputBase-input': { paddingTop: '10px', paddingBottom: '10px' } }}
                    />
                )}
            />
        </TooltipFormControl>

    if (property.type === "dateTime")
        return <TooltipFormControl property={property} >
            <DateTimePicker
                readOnly={property.readOnly}
                label={label}
                value={getDateTimeValue(value || property.default)}
                onChange={handleChange}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        data-testid={`field-${property.name}`}
                        aria-label={`field-${property.name}`}
                        sx={{ width: '100%', '& .MuiInputBase-input': { paddingTop: '10px', paddingBottom: '10px' } }}
                    />
                )}
            />
        </TooltipFormControl>

    if (property.type === "time")
        return <TooltipFormControl property={property} >
            <TimePicker
                readOnly={property.readOnly}
                label={label}
                value={getDateTimeValue(value || property.default)}
                onChange={handleChange}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        data-testid={`field-${property.name}`}
                        aria-label={`field-${property.name}`}
                        sx={{ width: '100%', '& .MuiInputBase-input': { paddingTop: '10px', paddingBottom: '10px' } }}
                    />
                )}
            />
        </TooltipFormControl>

    return null;
}

function getDateTimeValue(value?: string | number | boolean | Date): Date | null {
    if (!value) return null;

    switch (typeof value) {
        case "boolean":
            return null;
        case "number":
            return new Date(value);
        case "string":
        default: {
            const date = new Date(value);
            if (isNaN(date.getTime()))
                return null;
            return date;
        }
    }
}